import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import {
  FormattedMessage,
  FormattedDate,
  FormattedTime,
  useIntl,
} from 'react-intl';

import {
  getSavedTimezone,
  timezoneCode,
  convertDateTimezone,
} from 'ui_system/utilities/timezoneUtils';
import { localeDateFormats } from 'views/plugins/tours/locales/localMomentDateFormats';
import { BrandLink } from 'views/plugins/confirmation/styles';

import { Location } from './location';
import { ConfirmationSection } from './pending';
import ConfirmEmail from './ConfirmEmail';
import AdditionalGuestsLink from './AdditionalGuestsLink.tsx';
import ExperienceLink from './ExperienceLink.tsx';

const ConfirmationAccepted = (props) => {
  const intl = useIntl();
  const {
    first_name,
    email,
    date,
    time,
    footer_content,
    ticketed_event,
    online,
    tourTimezone,
    isoTime,
    has_multiple_tickets,
    booking_id_hash: bookingIdHash,
    booking_id: bookingId,
    displayError,
    setEmail,
    disable_guest_confirmation_email,
    is_live_booking: isLiveBooking,
    isBookingAffiliate,
    custom_title,
    headline,
    sub_headline,
    custom_body,
    location,
    custom_locations_enabled,
    experience_link,
    canAdditionalGuestsLink,
    AdditionalGuestsLinkSettings,
    automatic_booking,
    guest_paid_invoice,
    guest_reschedulable,
    has_future_availability,
    link_to_modify,
    numberOfGuests,
    maxNumberGuests,
    affiliateName,
    isCapacityPerTicket,
    isSbp,
  } = props;

  const renderDateReminder = () => {
    // Parse the date and time strings into a JavaScript Date object
    const dateObject = moment(date, 'MMM DD, YYYY');
    const timeObject = moment(time, 'h:mma');

    // Combine date and time into a single Date object
    const dateTime = moment(dateObject)
      .hours(timeObject.hours())
      .minutes(timeObject.minutes())
      .toDate();

    if (online) {
      const timezone = getSavedTimezone();
      const convertedDateTime = convertDateTimezone({
        originalTimezone: tourTimezone,
        timezone,
        date: date.trim(),
        dateFormat: 'MMM DD, YYYY',
        time: time.replace(/\s+/g, ''),
        timeFormat: 'h:mma',
      });

      const convertedDate = convertedDateTime.toDate();
      const matchingTimezoneCode = timezone ? timezoneCode(timezone) : '';

      return (
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="We look forward to seeing you on {date} at {time} ({timezone})"
            description="Message with booking date, time and timezone for online bookings"
            values={{
              date: (
                <FormattedDate
                  value={convertedDate}
                  year="numeric"
                  month="short"
                  day="2-digit"
                />
              ),
              time: (
                <FormattedTime
                  value={convertedDate}
                  hour="numeric"
                  minute="2-digit"
                  hour12={!isoTime}
                />
              ),
              timezone: matchingTimezoneCode,
            }}
          />
        </ConfirmationSection>
      );
    }

    return (
      <ConfirmationSection>
        <FormattedMessage
          defaultMessage="We look forward to seeing you on {date} at {time}"
          description="Message with booking date and time"
          values={{
            date: (
              <FormattedDate
                value={dateTime}
                year="numeric"
                month="short"
                day="2-digit"
              />
            ),
            time: (
              <FormattedTime
                value={dateTime}
                hour="numeric"
                minute="2-digit"
                hour12={!isoTime}
              />
            ),
          }}
        />
      </ConfirmationSection>
    );
  };

  const renderTicketedText = () => {
    return (
      <div>
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="We just sent an email to you with your {ticketCount, plural, one {ticket} other {tickets}}. Please present {ticketCount, plural, one {this ticket} other {your tickets}} when you arrive."
            description="Confirmation that ticket email was sent with instructions to present on arrival"
            values={{ ticketCount: has_multiple_tickets ? 2 : 1 }}
          />
        </ConfirmationSection>
        <ConfirmEmail
          email={email}
          bookingIdHash={bookingIdHash}
          bookingId={bookingId}
          displayError={displayError}
          setEmail={setEmail}
        />
        {renderDateReminder()}
      </div>
    );
  };

  const renderDefaultText = () => {
    if (isLiveBooking) return null;
    return (
      <div>
        {disable_guest_confirmation_email ? (
          <ConfirmationSection>
            <FormattedMessage
              defaultMessage="No reservation codes. No tickets to print."
              description="Message explaining no physical tickets needed"
            />
          </ConfirmationSection>
        ) : (
          <div>
            <ConfirmationSection>
              <FormattedMessage
                defaultMessage="We just sent an email to you with all your details. No reservation codes. No tickets to print."
                description="Confirmation email sent with no physical tickets needed"
              />
            </ConfirmationSection>
            <ConfirmEmail
              email={email}
              bookingIdHash={bookingIdHash}
              bookingId={bookingId}
              displayError={displayError}
              setEmail={setEmail}
            />
          </div>
        )}
        {renderDateReminder()}
      </div>
    );
  };

  const renderAffiliateText = () => {
    return (
      <div>
        <ConfirmationSection>
          <FormattedMessage
            defaultMessage="We just sent an email to you with all your details."
            description="Confirmation that details email was sent"
          />
        </ConfirmationSection>
      </div>
    );
  };

  const renderAffiliateTitle = () => {
    return (
      <div>
        <h1>
          <span className="titleRow">
            <FormattedMessage
              defaultMessage="Thank you, {name}."
              description="Thank you message with affiliate name"
              values={{ name: affiliateName }}
            />
          </span>
          <span className="titleRow" data-testid="confirmed-booking">
            <FormattedMessage
              defaultMessage="Your booking is confirmed."
              description="Booking confirmation message"
            />
          </span>
        </h1>
      </div>
    );
  };

  const renderRegularBody = () => {
    let renderFunction;
    if (isBookingAffiliate) {
      renderFunction = renderAffiliateText();
    } else if (ticketed_event) {
      renderFunction = renderTicketedText();
    } else {
      renderFunction = renderDefaultText();
    }

    return (
      <div className="Confirmation-Body-Regular-Content">{renderFunction}</div>
    );
  };

  const renderDefaultTitle = () => {
    return (
      <div>
        <h1>
          {isLiveBooking ? (
            <>
              <span className="titleRow">
                {headline || (
                  <FormattedMessage
                    defaultMessage="Thank you, {name}."
                    description="Thank you message with first name"
                    values={{ name: first_name }}
                  />
                )}
              </span>
              {sub_headline ? (
                <p
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(sub_headline, {
                      ALLOWED_TAGS: [
                        'a',
                        'b',
                        'i',
                        'em',
                        'strong',
                        'span',
                        'br',
                      ],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
                    }),
                  }}
                />
              ) : (
                <p>
                  <FormattedMessage
                    defaultMessage="You are all set"
                    description="Confirmation that everything is ready"
                  />
                </p>
              )}
            </>
          ) : (
            <>
              <span className="titleRow">
                <FormattedMessage
                  defaultMessage="Thank you, {name}."
                  description="Thank you message with first name"
                  values={{ name: first_name }}
                />
              </span>
              <span className="titleRow" data-testid="confirmed-booking">
                <FormattedMessage
                  defaultMessage="Your booking is confirmed."
                  description="Booking confirmation message"
                />
              </span>
            </>
          )}
        </h1>
      </div>
    );
  };

  const renderCustomTitle = (customTitle) => {
    const sanitizedTitle = DOMPurify.sanitize(customTitle, {
      ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'span', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
    return (
      <h1
        className="contains-brand-link"
        dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
      />
    );
  };

  const renderTitle = () => {
    let renderFunction;
    if (isBookingAffiliate) {
      renderFunction = renderAffiliateTitle();
    } else if (custom_title) {
      renderFunction = renderCustomTitle(custom_title);
    } else {
      renderFunction = renderDefaultTitle();
    }

    return (
      <div className="Confirmation-Title" data-testid="tour-confirmation-title">
        {renderFunction}
      </div>
    );
  };

  const belowGuestLimit = () => {
    return maxNumberGuests === 0 ? true : numberOfGuests <= maxNumberGuests;
  };

  const renderExperienceLink = (bookingLanguage) => {
    const {
      title,
      subtext,
      url,
      button_text,
      multi_guest_title,
      multi_guest_subtext,
      multi_guest_button_text,
    } = experience_link;
    return (
      <ExperienceLink
        title={title}
        subtext={subtext}
        url={url}
        buttonText={button_text}
        multiGuestTitle={multi_guest_title}
        multiGuestSubtext={multi_guest_subtext}
        multiGuestButtonText={multi_guest_button_text}
        multiGuestExperienceLink={canAdditionalGuestsLink && belowGuestLimit()}
        bookingIdHash={bookingIdHash}
        bookingLanguage={bookingLanguage}
      />
    );
  };

  const modifyBookingLink = () => {
    const nonReschedulable =
      !guest_reschedulable ||
      (!automatic_booking && !guest_paid_invoice) ||
      !has_future_availability ||
      isBookingAffiliate ||
      isCapacityPerTicket ||
      isSbp;

    if (nonReschedulable) return null;
    return (
      <BrandLink
        target="_blank"
        className="modifyBookingLink brand-color"
        href={link_to_modify}
        data-testid="reschedule-booking"
      >
        <FormattedMessage
          defaultMessage="Reschedule booking"
          description="Link text to reschedule a booking"
        />{' '}
        &rarr;
      </BrandLink>
    );
  };

  const modifyLink = modifyBookingLink();
  const showSeparator = modifyLink || footer_content;

  const queryParams = new URLSearchParams(window.location.search);
  const bookingLanguage = queryParams.get('lang');

  return (
    <div className="Confirmation-Body">
      {renderTitle()}
      <div className="Confirmation-Body">
        {!isLiveBooking && (
          <div>
            {renderRegularBody()}
            {custom_body && (
              <ConfirmationSection
                className="contains-brand-link"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(custom_body, {
                    ALLOWED_TAGS: [
                      'a',
                      'b',
                      'i',
                      'em',
                      'strong',
                      'span',
                      'br',
                      'p',
                      'div',
                      'ul',
                      'ol',
                      'li',
                    ],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
                  }),
                }}
              />
            )}
            {custom_locations_enabled && !online && (
              <Location location={location} />
            )}
            {experience_link &&
              !isBookingAffiliate &&
              renderExperienceLink(bookingLanguage)}
            {!experience_link &&
              canAdditionalGuestsLink &&
              belowGuestLimit() && (
                <AdditionalGuestsLink
                  heading={AdditionalGuestsLinkSettings.heading}
                  content={AdditionalGuestsLinkSettings.content}
                  buttonText={AdditionalGuestsLinkSettings.button_text}
                  bookingIdHash={bookingIdHash}
                  bookingLanguage={bookingLanguage}
                />
              )}
            {showSeparator && <hr className="separator" />}
            {modifyLink}
          </div>
        )}
        {footer_content}
      </div>
    </div>
  );
};

ConfirmationAccepted.displayName = 'ConfirmationAccepted-Plugins';

ConfirmationAccepted.propTypes = {
  first_name: PropTypes.string.isRequired,
  brand_color: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  footer_content: PropTypes.node,
  ticketed_event: PropTypes.bool.isRequired,
  online: PropTypes.bool,
  tourTimezone: PropTypes.string,
  isoTime: PropTypes.bool,
  has_multiple_tickets: PropTypes.bool,
  booking_id_hash: PropTypes.string,
  booking_id: PropTypes.number,
  displayError: PropTypes.bool,
  setEmail: PropTypes.func,
  disable_guest_confirmation_email: PropTypes.bool,
  is_live_booking: PropTypes.bool,
  isBookingAffiliate: PropTypes.bool,
  custom_title: PropTypes.string,
  headline: PropTypes.string,
  sub_headline: PropTypes.string,
  custom_body: PropTypes.string,
  location: PropTypes.object,
  custom_locations_enabled: PropTypes.bool,
  experience_link: PropTypes.object,
  canAdditionalGuestsLink: PropTypes.bool,
  AdditionalGuestsLinkSettings: PropTypes.object,
  automatic_booking: PropTypes.bool,
  guest_paid_invoice: PropTypes.bool,
  guest_reschedulable: PropTypes.bool,
  has_future_availability: PropTypes.bool,
  link_to_modify: PropTypes.string,
  numberOfGuests: PropTypes.number,
  maxNumberGuests: PropTypes.number,
  affiliateName: PropTypes.string,
};

export default ConfirmationAccepted;
